import { create } from 'zustand';
import type { ReviewIngredient, SubrecipeReviewReport } from '../types/reviewTypes';

/**
 * Subrecipe template-import review store.
 *
 * Holds the reviewReport returned by POST /api/subrecipes/createFromTemplate plus
 * the admin's review progress (current card, per-subrecipe status, per-ingredient
 * actions). Persisted to sessionStorage keyed by restaurantId so a page refresh
 * resumes mid-review. Never persisted to the DB.
 *
 * Uses the shared import-review row shapes
 * (types/reviewTypes.ts) and their page chrome (ReviewShell), but keep separate
 * progress so running one never disturbs the other.
 */

export type SubrecipeReviewStatus = 'pending' | 'accepted' | 'flagged';

interface PersistedReview {
  reviewReport: SubrecipeReviewReport;
  /** Subrecipes the import skipped because the name already existed. */
  skippedExisting: string[];
  currentIndex: number;
  statuses: Record<string, SubrecipeReviewStatus>;
}

const storageKey = (restaurantId: string) => `subrecipeAutomationReview:${restaurantId}`;

function persist(state: PersistedReview | null) {
  if (!state?.reviewReport) return;
  try {
    sessionStorage.setItem(storageKey(state.reviewReport.restaurantId), JSON.stringify(state));
  } catch (error) {
    console.error('Failed to persist subrecipe review to sessionStorage:', error);
  }
}

function readPersisted(restaurantId: string): PersistedReview | null {
  try {
    const raw = sessionStorage.getItem(storageKey(restaurantId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedReview;
    if (!parsed?.reviewReport || parsed.reviewReport.restaurantId !== restaurantId) return null;
    return parsed;
  } catch (error) {
    console.error('Failed to read subrecipe review from sessionStorage:', error);
    return null;
  }
}

interface SubrecipeReviewStore {
  reviewReport: SubrecipeReviewReport | null;
  skippedExisting: string[];
  currentIndex: number;
  statuses: Record<string, SubrecipeReviewStatus>;

  // Store a fresh report (new import) and reset review progress.
  setReport: (report: SubrecipeReviewReport, skippedExisting: string[]) => void;
  // Restore a persisted review for this restaurant; clears in-memory state when
  // none exists (a report for another restaurant is simply ignored).
  hydrateForRestaurant: (restaurantId: string) => void;
  goToSubrecipe: (index: number) => void;
  setSubrecipeStatus: (subrecipeId: string, status: SubrecipeReviewStatus) => void;
  // Patch one ingredient of one subrecipe (after a remove/swap/quantity review edit).
  updateIngredient: (subrecipeId: string, ingredientIndex: number, patch: Partial<ReviewIngredient>) => void;
  // Patch the yield after the reviewer corrects the automation's guess.
  updatePortions: (subrecipeId: string, portions: number, portionsUnit: string) => void;
  // Drop the review entirely (finished or dismissed) incl. its sessionStorage entry.
  clear: () => void;
}

export const useSubrecipeReviewStore = create<SubrecipeReviewStore>((set, get) => ({
  reviewReport: null,
  skippedExisting: [],
  currentIndex: 0,
  statuses: {},

  setReport: (report, skippedExisting) => {
    const statuses: Record<string, SubrecipeReviewStatus> = {};
    report.subrecipes.forEach(s => { statuses[s.subrecipeId] = 'pending'; });
    set({ reviewReport: report, skippedExisting, currentIndex: 0, statuses });
    persist({ reviewReport: report, skippedExisting, currentIndex: 0, statuses });
  },

  hydrateForRestaurant: (restaurantId) => {
    const current = get().reviewReport;
    if (current?.restaurantId === restaurantId) return;
    const persisted = readPersisted(restaurantId);
    if (persisted) {
      set({
        reviewReport: persisted.reviewReport,
        skippedExisting: persisted.skippedExisting ?? [],
        currentIndex: persisted.currentIndex,
        statuses: persisted.statuses,
      });
    } else {
      set({ reviewReport: null, skippedExisting: [], currentIndex: 0, statuses: {} });
    }
  },

  goToSubrecipe: (index) => {
    const { reviewReport, skippedExisting, statuses } = get();
    if (!reviewReport) return;
    // Allow index === subrecipes.length: that position renders the summary screen.
    const clamped = Math.max(0, Math.min(index, reviewReport.subrecipes.length));
    set({ currentIndex: clamped });
    persist({ reviewReport, skippedExisting, currentIndex: clamped, statuses });
  },

  setSubrecipeStatus: (subrecipeId, status) => {
    const { reviewReport, skippedExisting, currentIndex, statuses } = get();
    const next = { ...statuses, [subrecipeId]: status };
    set({ statuses: next });
    if (reviewReport) persist({ reviewReport, skippedExisting, currentIndex, statuses: next });
  },

  updateIngredient: (subrecipeId, ingredientIndex, patch) => {
    const { reviewReport, skippedExisting, currentIndex, statuses } = get();
    if (!reviewReport) return;
    const subrecipes = reviewReport.subrecipes.map(item => {
      if (item.subrecipeId !== subrecipeId) return item;
      const ingredients = item.ingredients.map((ing, i) => (i === ingredientIndex ? { ...ing, ...patch } : ing));
      return { ...item, ingredients };
    });
    const nextReport = { ...reviewReport, subrecipes };
    set({ reviewReport: nextReport });
    persist({ reviewReport: nextReport, skippedExisting, currentIndex, statuses });
  },

  updatePortions: (subrecipeId, portions, portionsUnit) => {
    const { reviewReport, skippedExisting, currentIndex, statuses } = get();
    if (!reviewReport) return;
    const subrecipes = reviewReport.subrecipes.map(item => (
      item.subrecipeId === subrecipeId
        // Once the reviewer has set the yield themselves it is no longer a guess,
        // so the card stops warning about it.
        ? { ...item, portions, portionsUnit, portionsWasGuessed: false }
        : item
    ));
    const nextReport = { ...reviewReport, subrecipes };
    set({ reviewReport: nextReport });
    persist({ reviewReport: nextReport, skippedExisting, currentIndex, statuses });
  },

  clear: () => {
    const { reviewReport } = get();
    if (reviewReport) {
      try {
        sessionStorage.removeItem(storageKey(reviewReport.restaurantId));
      } catch (error) {
        console.error('Failed to clear subrecipe review from sessionStorage:', error);
      }
    }
    set({ reviewReport: null, skippedExisting: [], currentIndex: 0, statuses: {} });
  },
}));
